window.customFormFactory = (function () {
    function normalizeCustomFieldKeyPart(value) {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim().toLowerCase();
    }

    function customFieldDedupeKey(field) {
        // Requirement: dedupe when same type AND same name
        // "name" is typically fieldName; fallback to label for safety.
        const typePart = normalizeCustomFieldKeyPart(field && field.fieldType);
        const namePart = normalizeCustomFieldKeyPart((field && field.fieldName) || (field && field.label));
        return typePart + '|' + namePart;
    }

    function propagateDuplicateValues(customForm) {
        if (!customForm || !customForm.fields || !customForm.fields.length) {
            return;
        }

        const groups = {};

        customForm.fields.forEach(field => {
            const key = customFieldDedupeKey(field);
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(field);
        });

        Object.keys(groups).forEach(key => {
            const group = groups[key];
            if (!group || group.length < 2) {
                return;
            }

            const source = group.find(f => f.fieldValue !== null && f.fieldValue !== undefined && String(f.fieldValue).length > 0);
            if (!source) {
                return;
            }

            group.forEach(target => {
                target.fieldValue = source.fieldValue;
            });
        });
    }

    function updateCustomForm(appointmentTypes, customForm) {
        let newFields = [];
        const seenKeys = {};
        appointmentTypes.forEach(appointmentType => {
            appointmentType.extraInfo.fieldConfiguration.customFields
                .filter(field => field.isActive !== false)
                .forEach(field => {
                    let fieldId = "customForm_" + field.fieldName + "_" + appointmentType.id.replaceAll("-", "")
                    const key = customFieldDedupeKey(field);
                    // Dedupe enkel op actieve velden; inactieve velden worden al uitgefilterd.
                    const isDuplicateForUi = !!seenKeys[key];
                    seenKeys[key] = true;
                    const newField = {
                        fieldId: fieldId,
                        fieldValue: undefined,
                        isUiDuplicate: isDuplicateForUi,
                        ...field
                    };
                    newFields.push(newField);
                    const existing = customForm.fields.find(x => x.fieldId === newField.fieldId);
                    if (!existing) {
                        // add new fields
                        customForm.fields.push(newField);
                    } else {
                        // keep existing.fieldValue, but refresh metadata & dedupe marker
                        existing.isUiDuplicate = newField.isUiDuplicate;
                        existing.fieldType = newField.fieldType;
                        existing.fieldName = newField.fieldName;
                        existing.label = newField.label;
                        existing.isRequiredForUser = newField.isRequiredForUser;
                        existing.isVisibleOnCalendar = newField.isVisibleOnCalendar;
                        existing.isActive = newField.isActive;
                        existing.listValues = newField.listValues;
                    }
                });
        });

        //remove nonexistingFields
        customForm.fields = customForm.fields
            .filter(oldField => newFields
                .some((newField => newField.fieldId === oldField.fieldId)))
            .filter(field => field.isActive !== false);
    }

    // TODO(DOCLEAS-026): remove this function and its call site below once the diagnostic
    // query in the ticket confirms 0 non-ISO customFormFields date values remain. Until then,
    // some stored date values are still DD-MM-YYYY (not yet backfilled to YYYY-MM-DD), and the
    // doclrDate directive's display formatter assumes ISO — feeding it DD-MM-YYYY silently
    // misparses it into a different-but-"valid" date instead of failing.
    function normalizeUnbackfilledDateValue(value) {
        if (moment(value, 'YYYY-MM-DD', true).isValid()) {
            return value;
        }
        const parsed = moment(value, 'DD-MM-YYYY', true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
    }

    function setValues(activity, customForm) {
        if (!activity.customFormFields) {
            return;
        }

        for (const fieldName in activity.customFormFields) {
            let fieldId = "customForm_" + fieldName + "_" + activity.id.replaceAll("-", "");
            const correspondingField = customForm.fields.filter(x => x.fieldId === fieldId);
            if (correspondingField.length > 0) {
                let value = activity.customFormFields[fieldName];
                if (correspondingField[0].fieldType === 'date' && value) {
                    value = normalizeUnbackfilledDateValue(value);
                }
                correspondingField[0].fieldValue = value;
            }
        }
    }

    function getValues(appointmentTypeId, customForm) {
        const idToReplace = "_" + appointmentTypeId.replaceAll("-", "");

        const customFormFields = {};

        customForm.fields
            .filter(field => field.fieldId.endsWith(idToReplace))
            .filter(field => field.isActive !== false)
            .forEach(field => {
                customFormFields[field.fieldName] = field.fieldValue
            });
        return customFormFields;
    }

    function getExtraReasonValues(appointmentTypeId, customForm) {
        const idToReplace = "_" + appointmentTypeId.replaceAll("-", "");

        const extraReasons = []

        customForm.fields
            .filter(field => field.fieldId.endsWith(idToReplace))
            .filter(field => field.isVisibleOnCalendar)
            .filter(field => field.isActive)
            .forEach(field => {
                if (field.fieldValue) {
                    extraReasons.push(String(field.fieldValue));
                }
            });
        return extraReasons;
    }

    function updateFieldConfig(appointmentTypes, fieldConfig) {

        fieldConfig.isFirstNameVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isFirstNameVisible).length > 0;

        fieldConfig.isFirstNameRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isFirstNameRequiredForUser).length > 0;

        fieldConfig.isNameVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isNameVisible).length > 0;

        fieldConfig.isNameRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isNameRequiredForUser).length > 0;

        fieldConfig.isPhoneVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isPhoneVisible).length > 0;

        fieldConfig.isPhoneRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isPhoneRequiredForUser).length > 0;

        fieldConfig.isSsinVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isSsinVisible).length > 0;

        fieldConfig.isSsinRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isSsinRequiredForUser).length > 0;

        fieldConfig.isBirthDateVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isBirthDateVisible).length > 0;

        fieldConfig.isBirthDateRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isBirthDateRequiredForUser).length > 0;

        fieldConfig.isMailVisible = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isMailVisible).length > 0;

        fieldConfig.isMailRequiredForUser = appointmentTypes
            .filter(x => x.extraInfo.fieldConfiguration.isMailRequiredForUser).length > 0;

    }

    function getListOptions(field, language) {
        return (field.listValues || []).map(function (item) {
            var translation = (item.translations || []).filter(function (t) { return t.language === language; })[0];
            if (!translation) {
                translation = (item.translations || [])[0];
            }
            return translation ? translation.value : undefined;
        }).filter(function (v) { return v !== undefined && v !== null; });
    }

    return {
        updateCustomForm: updateCustomForm,
        getValues: getValues,
        getExtraReasonValues: getExtraReasonValues,
        setValues: setValues,
        updateFieldConfig: updateFieldConfig,
        propagateDuplicateValues: propagateDuplicateValues,
        getListOptions: getListOptions
    };
})();